import { create } from "zustand";
import { supabase } from "../lib/supabase";

interface BlogState {
  posts: any[];
  totalPosts: number;
  currentPage: number;
  searchQuery: string;
  selectedTag: string | null;
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
  cache: Map<string, { data: any[]; count: number; timestamp: number }>;
  fetchPosts: (
    page?: number,
    query?: string,
    tag?: string,
    force?: boolean,
  ) => Promise<void>;
  setSearchQuery: (query: string) => void;
  setSelectedTag: (tag: string | null) => void;
  setPage: (page: number) => void;
  clearError: () => void;
  clearCache: () => void;
}

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useBlogStore = create<BlogState>((set, get) => ({
  posts: [],
  totalPosts: 0,
  currentPage: 1,
  searchQuery: "",
  selectedTag: null,
  loading: false,
  error: null,
  lastFetched: null,
  cache: new Map(),

  fetchPosts: async (page = 1, query = "", tag = null, force = false) => {
    const { cache } = get();
    const cacheKey = `${page}-${query}-${tag}`;
    const now = Date.now();

    // Check cache first
    if (!force) {
      const cached = cache.get(cacheKey);
      if (cached && now - cached.timestamp < CACHE_DURATION) {
        set({
          posts: cached.data,
          totalPosts: cached.count,
          currentPage: page,
          loading: false,
          lastFetched: cached.timestamp,
        });
        return;
      }
    }

    let retries = 0;

    while (retries < MAX_RETRIES) {
      try {
        set({ loading: true, error: null });

        let postsQuery = supabase
          .from("blog_posts")
          .select(
            `
            *,
            blog_authors (
              name,
              slug,
              image_url
            ),
            blog_categories (
              name,
              slug
            ),
            blog_tag_posts!left (
              blog_tags (
                id,
                name,
                slug
              )
            )
          `,
            { count: "exact" },
          )
          .eq("is_published", true)
          .eq("deleted", false)
          .order("published_at", { ascending: false });

        // Add search if query exists
        if (query) {
          postsQuery = postsQuery.or(
            `title.ilike.%${query}%,content.ilike.%${query}%`,
          );
        }

        // Add tag filter if tag exists
        if (tag) {
          postsQuery = postsQuery.eq("blog_tag_posts.blog_tags.name", tag);
        }

        // Add pagination
        const limit = 10;
        const from = (page - 1) * limit;
        const to = from + limit - 1;
        postsQuery = postsQuery.range(from, to);

        const { data: posts, count, error } = await postsQuery;

        if (error) {
          throw new Error(`Failed to fetch posts: ${error.message}`);
        }

        if (!posts) {
          throw new Error("No posts returned from database");
        }

        // Cache the result
        cache.set(cacheKey, {
          data: posts,
          count: count || 0,
          timestamp: now,
        });

        set({
          posts: posts,
          totalPosts: count || 0,
          currentPage: page,
          loading: false,
          lastFetched: now,
          cache,
        });

        // Success - exit retry loop
        break;
      } catch (error) {
        console.warn(`Fetch attempt ${retries + 1} failed:`, error);

        // On last retry, set error state
        if (retries === MAX_RETRIES - 1) {
          console.error("Error in fetchPosts:", error);
          set({
            error:
              error instanceof Error
                ? error.message
                : "An unexpected error occurred while loading blog posts",
            loading: false,
            posts: [],
            totalPosts: 0,
          });
          return;
        }

        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        retries++;
      }
    }
  },

  setSearchQuery: (query: string) => {
    set({ searchQuery: query });
    get().fetchPosts(1, query, get().selectedTag);
  },

  setSelectedTag: (tag: string | null) => {
    set({ selectedTag: tag });
    get().fetchPosts(1, get().searchQuery, tag);
  },

  setPage: (page: number) => {
    get().fetchPosts(page, get().searchQuery, get().selectedTag);
  },

  clearError: () => set({ error: null }),

  clearCache: () => {
    set({ cache: new Map() });
  },
}));
